import React, { useState } from 'react'
import { isFunction } from 'lodash'
import ConfirmDialog from '../common/ConfirmDialog/ConfirmDialog'

/**
 * @typedef {Object} ConfirmDialogHook
 * @property {function} openConfirmDialog Open dialog state change trigger
 * @property {function} closeConfirmDialog Close dialog state change trigger
 * @property {(event: React.MouseEvent<HTMLButtonElement>) => void} handleOpenClick Open click handler
 * @property {JSX.Element} ConfirmDialogComponent Confirm dialog FC component
 */

/**
 * @typedef {Object} ConfirmDialogProps
 * @property {function} cancelCallback Callback that triggers when action is cancelled.
 * @property {function} acceptCallback Callback that triggers when action is accepted.
 * @property {string} acceptLabel Accept action button label.
 * @property {string} cancelLabel Cancel action button label.
 * @property {string} actionMessage Action confirmation message.
 * @property {string=} dialogTitle Dialog title label
 * @property {string=} submitBtnClasses Custom css classes for submit button
 * @property {string=} cancelBtnClasses Custom css classes for cancel button
 */

/**
 * Confirm dialog hook expose API to control confirmation dialog actions.
 * @param {ConfirmDialogProps} props
 * @return {ConfirmDialogHook}
 */
export default function useConfirmDialog({
  acceptCallback,
  acceptLabel,
  actionMessage,
  cancelCallback,
  cancelLabel,
  dialogTitle= '',
  submitBtnClasses = '',
  cancelBtnClasses = ''
}) {
  const [open, setOpen] = useState(false)

  /**
   * Handle confirm dialog open.
   */
  const openConfirmDialog = () => {
    setOpen(true)
  }

  /**
   * Handle confirm dialog close.
   */
  const closeConfirmDialog = () => {
    setOpen(false)
  }

  /**
   * Handle button to open confirmation dialog.
   * @param {React.MouseEvent<HTMLButtonElement>} event
   */
  const handleOpenClick = (event) => {
    openConfirmDialog()
  }

  /**
   * Handle button to close confirmation dialog.
   * @param {React.MouseEvent<HTMLButtonElement>} event
   */
  const handleCloseClick = (event) => {
    closeConfirmDialog()
  }

  /**
   * Trigger action accept callback and close dialog.
   */
  const handleAcceptAction = () => {
    if (isFunction(acceptCallback)) {
      acceptCallback()
    }

    closeConfirmDialog()
  }

  /**
   * Trigger action cancel callback and close dialog.
   */
  const handleCancelAction = () => {
    if (isFunction(cancelCallback)) {
      cancelCallback()
    }

    closeConfirmDialog()
  }

  /**
   * Confirm dialog component.
   * @return {JSX.Element}
   */
  const ConfirmDialogComponent = (
    <ConfirmDialog
      open={open}
      acceptCallback={handleAcceptAction}
      cancelCallback={handleCancelAction}
      handleCloseClick={handleCloseClick}
      acceptLabel={acceptLabel}
      cancelLabel={cancelLabel}
      actionMessage={actionMessage}
      dialogTitle={dialogTitle}
      submitBtnClasses={submitBtnClasses}
      cancelBtnClasses={cancelBtnClasses}
    />
  )

  return {
    openConfirmDialog,
    closeConfirmDialog,
    ConfirmDialogComponent,
    handleOpenClick,
  }
}
